/**
 * Mapa central de evento → queries do React Query que devem ser invalidadas.
 *
 * Concentrar essa regra evita que cada hook invente sua própria invalidação
 * (e esqueça telas) e garante ressincronização consistente após reconexão.
 */
import { EVENT_TYPES } from './eventTypes';

export const QUERY_KEYS = Object.freeze({
  PEDIDOS: ['pedidos'],
  USER_PEDIDOS: ['userPedidos'],
  DASHBOARD: ['dashboard'],
  USER_DASHBOARD: ['userDashboard'],
  CLIENTES: ['clientes'],
  PAGAMENTOS: ['payments'],
  NOTIFICACOES: ['notificacoes'],
  NOTIFICACOES_NAO_LIDAS: ['notificacoes', 'unread'],
});

export const QUERY_KEYS_BY_EVENT = Object.freeze({
  [EVENT_TYPES.PEDIDO_CRIADO]: ['PEDIDOS', 'USER_PEDIDOS', 'DASHBOARD', 'USER_DASHBOARD'],
  [EVENT_TYPES.PEDIDO_ATUALIZADO]: ['PEDIDOS', 'USER_PEDIDOS', 'DASHBOARD', 'USER_DASHBOARD'],
  [EVENT_TYPES.PEDIDO_STATUS_ALTERADO]: ['PEDIDOS', 'USER_PEDIDOS', 'DASHBOARD', 'USER_DASHBOARD'],
  [EVENT_TYPES.PEDIDO_ARQUIVOS_LIBERADOS]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
  [EVENT_TYPES.PEDIDO_VISUALIZADO]: ['PEDIDOS', 'USER_PEDIDOS'],
  [EVENT_TYPES.PAGAMENTO_ATUALIZADO]: [
    'PEDIDOS',
    'USER_PEDIDOS',
    'PAGAMENTOS',
    'DASHBOARD',
    'USER_DASHBOARD',
  ],
  [EVENT_TYPES.CLIENTE_CRIADO]: ['CLIENTES', 'DASHBOARD'],
  [EVENT_TYPES.CLIENTE_ATUALIZADO]: ['CLIENTES'],
  [EVENT_TYPES.CLIENTE_REMOVIDO]: ['CLIENTES'],
  [EVENT_TYPES.NOTIFICACAO_NOVA]: ['NOTIFICACOES', 'NOTIFICACOES_NAO_LIDAS'],
  [EVENT_TYPES.NOTIFICACAO_LIDA]: ['NOTIFICACOES', 'NOTIFICACOES_NAO_LIDAS'],
  [EVENT_TYPES.NOTIFICACAO_ESTADO]: ['NOTIFICACOES'],
  [EVENT_TYPES.NOTIFICACAO_REMOVIDA]: ['NOTIFICACOES', 'NOTIFICACOES_NAO_LIDAS'],
  [EVENT_TYPES.ARQUIVO_UPLOAD_CONCLUIDO]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
  [EVENT_TYPES.SELECAO_CONFIRMADA]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
  [EVENT_TYPES.SELECAO_REABERTA]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
  [EVENT_TYPES.SELECAO_PASTA_ATIVADA]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
  [EVENT_TYPES.SELECAO_PASTA_DESATIVADA]: ['PEDIDOS', 'USER_PEDIDOS', 'USER_DASHBOARD'],
});

const resolveQueryKey = (keyName) => QUERY_KEYS[keyName] || [keyName];

/**
 * Invalida as queries conhecidas de um evento.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} eventType Nome canônico do evento.
 * @param {{extraKeys?: string[]}} [options]
 * @returns {string[]} Prefixos invalidados.
 */
export const invalidateQueriesForEvent = (queryClient, eventType, options = {}) => {
  if (!queryClient || typeof queryClient.invalidateQueries !== 'function') return [];

  const keyNames = new Set([
    ...(QUERY_KEYS_BY_EVENT[eventType] || []),
    ...(options.extraKeys || []),
  ]);
  const prefixes = [];

  for (const keyName of keyNames) {
    const [prefix] = resolveQueryKey(keyName);
    if (!prefix) continue;
    if (prefixes.includes(prefix)) continue;

    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) && String(query.queryKey[0]) === String(prefix),
    });
    prefixes.push(prefix);
  }

  return prefixes;
};

/** Ressincroniza tudo que é alimentado por tempo real (usado ao reconectar). */
export const invalidateAllKnownQueries = (queryClient) => {
  if (!queryClient || typeof queryClient.invalidateQueries !== 'function') return [];

  const prefixes = [...new Set(Object.values(QUERY_KEYS).map((queryKey) => queryKey[0]))];

  for (const prefix of prefixes) {
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) && String(query.queryKey[0]) === String(prefix),
    });
  }

  return prefixes;
};
